/**
 * BoardV3 Timeline Header
 * Multi-row sticky header: Jahre, Monate, KW, Tage.
 * Self-contained: reads visibleRows/isResizing from boardLayoutStore,
 * manages header resize and context menu internally.
 */

import React, { memo, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DAYS_PER_WEEK } from '../../utils/boardHelpers';
import {
  computeMonthSpans,
  computeYearSpans,
} from './timelineHeaderHelpers';
import { useDayWidth, useHeaderHeight, useVisibleWeeks, useVisibleHeaderRows, useIsHeaderResizing, useHeaderControls } from '../BoardLayoutContext';
import { useBoardStartDate } from '../../../stores/hooks';
import { HeaderContextMenu } from '../menus/HeaderContextMenu';
import { TimelineYearRow } from './TimelineYearRow';
import { TimelineMonthRow } from './TimelineMonthRow';
import { TimelineKwRow } from './TimelineKwRow';
import { TimelineDayRow } from './TimelineDayRow';

interface BoardV3TimelineHeaderProps {
  onZoomDragStart?: (anchorDay: number, e: React.MouseEvent) => void;
  isZoomDragging?: boolean;
}

// ============================================
// Zoom Handle at KW boundary
// ============================================

const ZoomHandle = memo(function ZoomHandle({
  x,
  height,
  anchorDay,
  onZoomDragStart,
  isZoomDragging,
}: {
  x: number;
  height: number;
  anchorDay: number;
  onZoomDragStart: (anchorDay: number, e: React.MouseEvent) => void;
  isZoomDragging?: boolean;
}) {
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    onZoomDragStart(anchorDay, e);
  }, [anchorDay, onZoomDragStart]);

  return (
    <div
      onMouseDown={handleMouseDown}
      style={{
        position: 'absolute',
        left: x - 3,
        top: 0,
        width: 6,
        height,
        cursor: 'col-resize',
        zIndex: 22,
        backgroundColor: isZoomDragging ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
      }}
      onMouseEnter={(e) => {
        if (!isZoomDragging) {
          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isZoomDragging) {
          e.currentTarget.style.backgroundColor = isZoomDragging ? 'rgba(59, 130, 246, 0.1)' : 'transparent';
        }
      }}
    />
  );
});

// ============================================
// Main Component
// ============================================

export const BoardV3TimelineHeader = memo(function BoardV3TimelineHeader({
  onZoomDragStart,
  isZoomDragging,
}: BoardV3TimelineHeaderProps) {
  const visibleWeeks = useVisibleWeeks();
  const dayWidth = useDayWidth();
  const headerHeight = useHeaderHeight();
  const visibleRows = useVisibleHeaderRows();
  const isResizing = useIsHeaderResizing();
  const boardStartDate = useBoardStartDate();
  const weekWidth = dayWidth * DAYS_PER_WEEK;

  const headerResize = useHeaderControls();

  // Header context menu state (local)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const { i18n } = useTranslation();
  const locale = i18n.language;

  const monthSpans = useMemo(
    () => computeMonthSpans(visibleWeeks, boardStartDate, locale),
    [visibleWeeks, boardStartDate, locale],
  );

  const yearSpans = useMemo(
    () => computeYearSpans(visibleWeeks, boardStartDate),
    [visibleWeeks, boardStartDate],
  );

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          height: headerHeight,
          backgroundColor: '#fff',
          borderBottom: '1px solid #e2e8f0',
          zIndex: 20,
          overflow: 'hidden',
        }}
        onContextMenu={handleContextMenu}
      >
        {visibleRows.map((rowId, rowIndex) => {
          const isLast = rowIndex === visibleRows.length - 1;

          switch (rowId) {
            case 'jahre':
              return <TimelineYearRow key="jahre" yearSpans={yearSpans} dayWidth={dayWidth} isLast={isLast} />;
            case 'monate':
              return <TimelineMonthRow key="monate" monthSpans={monthSpans} dayWidth={dayWidth} isLast={isLast} />;
            case 'kw':
              return <TimelineKwRow key="kw" visibleWeeks={visibleWeeks} dayWidth={dayWidth} weekWidth={weekWidth} isLast={isLast} />;
            case 'tage':
              return <TimelineDayRow key="tage" visibleWeeks={visibleWeeks} dayWidth={dayWidth} isLast={isLast} />;
            default:
              return null;
          }
        })}

        {/* Zoom resize handles at KW boundaries */}
        {onZoomDragStart && visibleWeeks.map(({ kw, year, startDay }) => {
          const boundaryDay = startDay + DAYS_PER_WEEK;
          return (
            <ZoomHandle
              key={`zh-${year}-${kw}`}
              x={boundaryDay * dayWidth}
              height={headerHeight}
              anchorDay={boundaryDay}
              onZoomDragStart={onZoomDragStart}
              isZoomDragging={isZoomDragging}
            />
          );
        })}

        {/* Resize handle at the bottom */}
        <div
          onMouseDown={headerResize.handleResizeStart}
          style={{
            position: 'absolute',
            bottom: -2,
            left: 0,
            right: 0,
            height: 4,
            cursor: 'row-resize',
            zIndex: 21,
            backgroundColor: isResizing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!isResizing) {
              e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        />
      </div>

      {contextMenu && (
        <HeaderContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          mode={headerResize.mode}
          isJahreVisible={headerResize.isRowVisible('jahre')}
          isMonateVisible={headerResize.isRowVisible('monate')}
          isTageVisible={headerResize.isRowVisible('tage')}
          onToggleJahre={() => headerResize.toggleRow('jahre')}
          onToggleMonate={() => headerResize.toggleRow('monate')}
          onToggleTage={() => headerResize.toggleRow('tage')}
          onSetAutoMode={() => headerResize.setAutoMode()}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
});
